import * as React from "react";
import { useEffect, useState } from "react";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import {
  getValidationSchema,
  initialValues as initialValuesBase,
} from "./config";
import {
  Box,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Switch,
} from "@mui/material";
import { StyledFormicError } from "./styles";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { Resource } from "types/resource";
import {
  useAddResourceMutation,
  useAddResourceTagMutation,
  useDeleteResourceTagByTagAndResourceMutation,
  useUpdateResourceMutation,
} from "graphql/__generated__/hooks";
import { Modal } from "components/Modal";
import { VisuallyHiddenInput } from "components/Input";

import { Messages, NotificationSeverity } from "helpers/constants";
import { uploadFile } from "reduxStore/files/thunks";
import { useAppDispatch } from "hooks/useAppDispatch";
import { Upload } from "@mui/icons-material";
import { TagManager } from "components/TagManager";
import { Tag } from "types/tag";
import { useNotifications } from "hooks/useNotifications";

export type Props = {
  createOrEditResourceModal: boolean;
  handleCreateOrEditResourceModalDismiss: () => void;
  handleCreateOrEditResourceChange: (resource?: Resource) => string | void;
  resource?: Resource | null;
};

const CreateOrEditResourceModal = ({
  createOrEditResourceModal,
  handleCreateOrEditResourceModalDismiss,
  handleCreateOrEditResourceChange,
  resource = null,
}: Props) => {
  const isEditMode = resource !== null;
  const [loading, setLoading] = useState(false);
  const [updateResource] = useUpdateResourceMutation();
  const [addResource] = useAddResourceMutation();
  const [uploading, setUploading] = useState(false);
  const [uploadFileError, setUploadFileError] = useState("");
  const dispatch = useAppDispatch();
  const [tags, setTags] = useState<Tag[]>([]);
  const [isUpload, setIsUpload] = useState<boolean>(true);
  const [addResourceTag] = useAddResourceTagMutation();
  const [deleteResourceTag] = useDeleteResourceTagByTagAndResourceMutation();
  const { notify } = useNotifications();

  const isEditing = !!resource;
  useEffect(() => {
    setTags(resource?.tags?.map((tag) => tag.tag) || []);
  }, [resource]);

  const handleCreateOrEditResource = async (reqValues: Resource) => {
    setLoading(true);
    await addResource({
      variables: {
        name: reqValues.name,
        description: reqValues.description,
        url: reqValues.url,
      },
    });
    handleCreateOrEditResourceChange();
    setLoading(false);
    notify({
      message: Messages.successResourceCreated,
      severity: NotificationSeverity.success,
    });
  };

  const handleEditResource = async (reqValues: Resource) => {
    setLoading(true);

    await updateResource({
      variables: {
        id: resource?.resource_id ?? "",
        resource: reqValues,
      },
    });
    handleCreateOrEditResourceChange();
    setLoading(false);
    notify({
      message: Messages.successResourceUpdated,
      severity: NotificationSeverity.success,
    });
  };

  const initialValues = isEditMode
    ? {
        resourceName: resource?.name || "",
        resourceDescription: resource?.description || "",
        resourceUrl: resource?.url || "",
      }
    : initialValuesBase;

  const onUploadFile = async (event: React.BaseSyntheticEvent) => {
    setUploading(true);
    const result = await dispatch(uploadFile(event.target.files[0]));
    if (result.meta.requestStatus === "rejected") {
      setUploadFileError(Messages.errorResourceUpload);
    } else {
      await formik.setFieldValue("resourceUrl", result?.payload?.filePath);
    }
    setUploading(false);
  };

  const handleAddTag = async (tag: Tag) => {
    await addResourceTag({
      variables: { tagId: tag.tag_id, resourceId: resource?.resource_id },
    });
    setTags([...tags, tag]);
  };

  const handleDeleteTag = async (tagToRemove: Tag) => {
    await deleteResourceTag({
      variables: {
        tagId: tagToRemove.tag_id,
        resourceId: resource?.resource_id,
      },
    });
    const newTags = tags.filter((tag) => tag.tag_id !== tagToRemove.tag_id);
    setTags(newTags);
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const reqValues = {
        name: values.resourceName,
        description: values.resourceDescription,
        url: values.resourceUrl,
      };
      if (isEditMode) {
        handleEditResource(reqValues as Resource).then();
      } else {
        handleCreateOrEditResource(reqValues as Resource).then();
      }
    },
  });

  return (
    <Modal
      open={createOrEditResourceModal}
      onClose={handleCreateOrEditResourceModalDismiss}
      title={isEditMode ? resource?.name : "Create a new Resource"}
      size="small"
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ mt: 1 }}>
            <Box>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="resourceName"
                label="Resource Name"
                type="text"
                id="resourceName"
                value={formik?.values?.resourceName}
              />
              <ErrorMessage name="resourceName" component={StyledFormicError} />
            </Box>

            <Box>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="resourceDescription"
                label="Resource Description"
                type="text"
                id="resourceName"
                value={formik?.values?.resourceDescription}
              />
              <ErrorMessage
                name="resourceDescription"
                component={StyledFormicError}
              />
            </Box>

            <FormControlLabel
              sx={{ mt: 2, color: "dimgray" }}
              control={
                <Switch
                  checked={isUpload}
                  onChange={() => {
                    setIsUpload(!isUpload);
                  }}
                />
              }
              label="Upload"
            />

            {!isUpload && (
              <Box mt={2}>
                <InputLabel>Add Your Resource URL</InputLabel>
                <TextField
                  variant="standard"
                  onChange={formik.handleChange}
                  margin="dense"
                  fullWidth
                  name="resourceUrl"
                  label="Resource URL"
                  type="text"
                  id="resourceUrl"
                  value={formik?.values?.resourceUrl}
                />
              </Box>
            )}

            {isUpload && (
              <Box mt={2}>
                <InputLabel>Upload Your Resource File</InputLabel>

                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid size="auto" sx={{ mt: 2.5 }}>
                    <Box
                      sx={{
                        position: "relative",
                        alignItems: "flex-end",
                        alignSelf: "flex-end",
                      }}
                    >
                      <Button
                        disabled={uploading}
                        component="label"
                        role={undefined}
                        variant="outlined"
                        tabIndex={-1}
                        startIcon={<Upload />}
                      >
                        Upload
                        <VisuallyHiddenInput
                          type="file"
                          onChange={onUploadFile}
                        />
                      </Button>
                      {uploadFileError !== "" && (
                        <Box mt={1} sx={{ color: "red" }}>
                          {uploadFileError}
                        </Box>
                      )}

                      {uploading && (
                        <CircularProgress
                          size={30}
                          sx={{
                            color: green[500],
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-12px",
                            marginLeft: "-12px",
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid size="grow">
                    <TextField
                      disabled
                      variant="standard"
                      onChange={formik.handleChange}
                      margin="dense"
                      fullWidth
                      name="resourceUrl"
                      label="Resource URL"
                      type="text"
                      id="resourceUrl"
                      value={formik?.values?.resourceUrl}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {isEditing && (
              <Box mt={2}>
                <FormLabel>Tags</FormLabel>
                <TagManager
                  tags={tags}
                  onAdd={handleAddTag}
                  onDelete={handleDeleteTag}
                  isEditing={isEditing}
                />
              </Box>
            )}

            <Grid sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Box mr={2}>
                <Button
                  variant="outlined"
                  onClick={handleCreateOrEditResourceModalDismiss}
                >
                  Cancel
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={loading}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
              {loading && (
                <CircularProgress
                  size={30}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Grid>
          </Box>
        </form>
      </FormikProvider>
    </Modal>
  );
};

export default CreateOrEditResourceModal;
